import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { documentTranslationApi } from '../api/documentTranslationApi';
import { UploadInitRequestBody } from '../../../../core/models/fileUpload/uploadInitRequestBody';
import { UploadChunkRequestBody } from '../../../../core/models/fileUpload/uploadChunkRequestBody';
import { TranslatableLanguageValueEnum } from '../../enums/translatableLanguageValueEnum';
import { documentTranslationUtils } from '../../utils/documentTranslationUtils';
import useDocumentTranslationStore from '../../store/documentTranslationStore';
import {
  LastUsedLanguageResponseModel,
  TranslatableLanguageModel,
} from '../../types';
/** Query key factory for document translation queries and mutations. */
export const documentTranslationKeys = {
  all: ['documentTranslation'] as const,
  options: () => [...documentTranslationKeys.all, 'options'] as const,
  lastUsedLanguage: () =>
    [...documentTranslationKeys.all, 'lastUsedLanguage'] as const,
  initiateUpload: () =>
    [...documentTranslationKeys.all, 'initiateUpload'] as const,
  uploadChunk: () => [...documentTranslationKeys.all, 'uploadChunk'] as const,
  startTranslation: () =>
    [...documentTranslationKeys.all, 'startTranslation'] as const,
  download: () => [...documentTranslationKeys.all, 'download'] as const,
  uploadAndTranslate: () =>
    [...documentTranslationKeys.all, 'uploadAndTranslate'] as const,
};

/**
 * Fetch available translation language options
 * Caches indefinitely as language options rarely change
 */
export const getTranslationOptionsQueryOptions = () =>
  queryOptions<TranslatableLanguageModel[]>({
    queryKey: documentTranslationKeys.options(),
    queryFn: () => documentTranslationApi.getTranslationOptionsAsync(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

/** Compatibility wrapper for incremental migration to query option factories. */
export const useGetTranslationOptions = () =>
  useQuery(getTranslationOptionsQueryOptions());

/**
 * Fetch the last used language preference
 * Caches indefinitely to minimize server calls
 */
export const getLastUsedLanguageQueryOptions = () =>
  queryOptions<LastUsedLanguageResponseModel>({
    queryKey: documentTranslationKeys.lastUsedLanguage(),
    queryFn: () => documentTranslationApi.getLastUsedLanguageAsync(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

/** Compatibility wrapper for incremental migration to query option factories. */
export const useGetLastUsedLanguage = () =>
  useQuery(getLastUsedLanguageQueryOptions());

/** Compatibility wrapper for incremental migration to query option factories. */
export const useGetTranslationLanguages = () => {
  const optionsQuery = useGetTranslationOptions();
  const lastUsedQuery = useGetLastUsedLanguage();

  return {
    languageOptions: optionsQuery.data ?? [],
    lastUsedLanguage: lastUsedQuery.data?.lastUsedLanguage,
    isLoading: optionsQuery.isLoading || lastUsedQuery.isLoading,
    isError: optionsQuery.isError || lastUsedQuery.isError,
    error: optionsQuery.error || lastUsedQuery.error,
  };
};

/**
 * Initiate document translation upload
 * Starts the translation process by creating a new translation session
 */
export const useInitiateDocumentTranslation = () => {
  return useMutation({
    mutationKey: documentTranslationKeys.initiateUpload(),
    mutationFn: (requestBody: UploadInitRequestBody) =>
      documentTranslationApi.initiateUpdateDocumentToTranslateAsync(
        requestBody
      ),
  });
};

/**
 * Upload a single chunk of a document
 * Handles chunked file uploads for large documents
 */
export const useUpdateDocumentChunk = () => {
  return useMutation({
    mutationKey: documentTranslationKeys.uploadChunk(),
    mutationFn: (requestBody: UploadChunkRequestBody) =>
      documentTranslationApi.updateDocumentToTranslateChunkAsync(requestBody),
  });
};

/**
 * Start the document translation process on the backend
 * Initiates the actual translation job after all chunks are uploaded
 */
export const useStartDocumentTranslation = () => {
  return useMutation({
    mutationKey: documentTranslationKeys.startTranslation(),
    mutationFn: ({
      translation_id,
      target_language,
    }: {
      translation_id: string;
      target_language: TranslatableLanguageValueEnum;
    }) =>
      documentTranslationApi.startDocumentTranslationAsync(
        translation_id,
        target_language
      ),
  });
};

/**
 * Download the translated document
 * Retrieves the translated file and triggers browser download
 */
export const useDownloadTranslatedDocument = () => {
  return useMutation({
    mutationKey: documentTranslationKeys.download(),
    mutationFn: (translation_id: string) =>
      documentTranslationApi.downloadTranslatedDocumentAsync(translation_id),
  });
};

interface UploadAndTranslateParams {
  file: File;
  targetLanguage: TranslatableLanguageValueEnum;
}

/**
 * Compound hook that orchestrates the entire document translation workflow:
 * 1. Initiates upload session
 * 2. Uploads file in chunks
 * 3. Starts translation on backend
 * 4. Polls for completion
 * 5. Downloads translated document
 */
export const useUploadAndTranslateDocument = () => {
  const queryClient = useQueryClient();
  const initiateMutation = useInitiateDocumentTranslation();
  const chunkMutation = useUpdateDocumentChunk();
  const startMutation = useStartDocumentTranslation();
  const downloadMutation = useDownloadTranslatedDocument();

  const setTranslationProgress = useDocumentTranslationStore(
    (state) => state.setTranslationProgress
  );
  const setFileDetails = useDocumentTranslationStore(
    (state) => state.setFileDetails
  );

  /**
   * Helper function to poll translation status until completion
   * Handles statuses: notstarted, running, failed, succeeded
   * Throws error with detailed message if translation fails
   */
  const pollTranslationStatus = async (
    translationId: string
  ): Promise<void> => {
    const maxAttempts = 120; // 10 minutes with 5-second interval
    let attempts = 0;

    while (attempts < maxAttempts) {
      // Wait 5 seconds before checking status
      await new Promise((resolve) => setTimeout(resolve, 5000));

      try {
        const response =
          await documentTranslationApi.checkTranslationStatusAsync(
            translationId
          );

        const status = response.status?.toLowerCase();

        if (status === 'succeeded') {
          return; // Translation complete, exit polling
        } else if (status === 'failed') {
          // Extract error message from Azure response if available
          const errorMessage =
            response.azure_response?.error?.message ||
            'Translation failed. Please try again.';
          throw new Error(errorMessage);
        }
        // Continue polling for notstarted, running, or unknown statuses
      } catch (error) {
        // Re-throw errors we created (failed status or network errors during this check)
        // Don't catch and ignore them - let them propagate to the mutation handler
        if (error instanceof Error) {
          throw error;
        }
        // If it's not an Error object, wrap it
        throw new Error(
          'An unexpected error occurred during translation status check'
        );
      }

      attempts++;
    }

    throw new Error('Translation polling timeout. Please try again.');
  };

  const mutate = async (params: UploadAndTranslateParams) => {
    // Step 1: Initiate upload session
    const initResponse = await initiateMutation.mutateAsync({
      file: params.file,
    });

    //update progress
    setTranslationProgress(
      'backendInit',
      100,
      Math.round((1 / 3) * 100),
      `File ${params.file.name} chunking in progress`
    );

    const translationId = initResponse.translation_id;

    // Step 2: Upload file in chunks
    const totalChunks = Math.ceil(
      params.file.size / documentTranslationApi.chunkSize
    );

    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * documentTranslationApi.chunkSize;
      const end = Math.min(
        start + documentTranslationApi.chunkSize,
        params.file.size
      );
      const chunk = params.file.slice(start, end);
      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('file_name', params.file.name);

      await chunkMutation.mutateAsync({
        translation_id: translationId,
        chunk_number: chunkNumber,
        form_data: formData,
      });
    }

    //update progress
    setTranslationProgress(
      'chunk',
      100,
      Math.round((2 / 3) * 100),
      `File ${params.file.name} translation in progress`
    );

    // Step 3: Start translation on backend
    await startMutation.mutateAsync({
      translation_id: translationId,
      target_language: params.targetLanguage,
    });

    // Step 4: Poll for completion
    // This will throw an error if translation fails
    await pollTranslationStatus(translationId);

    // Step 5: Download translated document
    const downloadResponse = await downloadMutation.mutateAsync(translationId);

    // Extract filename from Content-Disposition header
    const contentDisposition =
      downloadResponse.headers?.['content-disposition'];
    const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
    const filename = filenameMatch ? filenameMatch[1] : 'translated_file';

    // Trigger browser download
    documentTranslationUtils.downloadFileFromBlob(
      downloadResponse.data,
      filename
    );

    //update progress
    setFileDetails(downloadResponse.data, filename);
    setTranslationProgress(
      'isTranslated',
      100,
      Math.round((3 / 3) * 100),
      `File ${params.file.name} successfully translated. Click for details`
    );
  };

  return useMutation({
    mutationKey: documentTranslationKeys.uploadAndTranslate(),
    mutationFn: mutate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentTranslationKeys.lastUsedLanguage(),
      });
    },
    onError: () => {
      setTranslationProgress(
        'isTranslated',
        0,
        0,
        `File translation failed. Click for details`
      );
    },
    onSettled: () => {
      setTranslationProgress('isProcessComplete', 100, 0, '');
    },
  });
};
